import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { requestPasswordReset } from '../../api/owner'
import {
  AuthError,
  AuthField,
  AuthFields,
  AuthHeading,
  AuthShell,
  AuthSubmit,
  AuthSwitch,
  authInputClass,
  authLinkClass,
} from './AuthShell'

/**
 * Ronde 21G en 21H — een link aanvragen, en de bevestiging.
 *
 * Twee schermen in één component omdat het tweede alleen bestaat als gevolg van
 * het eerste: het e-mailadres uit het formulier staat erin, en dat wil je niet
 * via de URL doorgeven aan een aparte route.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Zoveel seconden voor je opnieuw mag. Elke aanvraag verbrandt de vorige
  // link, dus twee keer snel achter elkaar drukken levert twee mails op waarvan
  // de eerste al dood is — precies de mail die je waarschijnlijk het eerst
  // openklikt. De teller maakt duidelijk dat wachten de bedoeling is.
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (cooldown === 0) {
      return
    }

    const timer = setTimeout(() => setCooldown((left) => left - 1), 1000)

    return () => clearTimeout(timer)
  }, [cooldown])

  async function send(event?: FormEvent) {
    event?.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const address = email.trim()

      await requestPasswordReset(address)

      // De server zegt altijd hetzelfde, ook als dit adres niet bestaat. Dat
      // verschil verzwijgen is het hele punt van dit scherm: anders is het een
      // manier om af te lopen wie er een account heeft.
      setSentTo(address)
      setCooldown(60)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Versturen lukte niet.')
    } finally {
      setBusy(false)
    }
  }

  if (sentTo) {
    return (
      <AuthShell>
        <div className="mt-9 lg:mt-0">
          <span
            aria-hidden="true"
            className="bg-brand-coral text-brand-coral-ink mb-7 flex size-14 items-center justify-center rounded-full text-2xl"
          >
            ✓
          </span>

          <AuthHeading title="Kijk in je mailbox">
            We stuurden een link naar <span className="text-ink">{sentTo}</span>. De link werkt één
            uur.
          </AuthHeading>

          <button
            type="button"
            onClick={() => void send()}
            disabled={busy || cooldown > 0}
            className="border-brand-line font-brand text-brand-soft hover:bg-brand-line/40 mt-8 h-14 w-full rounded-full border text-[15px] font-medium transition-colors disabled:opacity-55 disabled:hover:bg-transparent"
          >
            {busy
              ? 'Bezig…'
              : cooldown > 0
                ? `Opnieuw versturen kan over ${cooldown}s`
                : 'Opnieuw versturen'}
          </button>

          {error && <AuthError>{error}</AuthError>}

          <p className="font-brand text-brand-faint mt-5 text-center text-[13px]">
            Niets ontvangen? Check je spam.
          </p>

          <AuthSwitch question="Weet je het weer?">
            <Link to="/beheer/aanmelden" className={authLinkClass}>
              Aanmelden
            </Link>
          </AuthSwitch>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell onBack={() => navigate('/beheer/aanmelden')}>
      <form onSubmit={(event) => void send(event)} className="mt-9 lg:mt-0">
        <AuthHeading title="Wachtwoord vergeten">
          Vul je e-mailadres in. We sturen je een link om een nieuw wachtwoord te kiezen.
        </AuthHeading>

        <AuthFields>
          <AuthField id="email" label="E-mail">
            <input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="lotte@voorbeeld.be"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={authInputClass}
            />
          </AuthField>

          <AuthSubmit busy={busy} disabled={!email.trim()}>
            Stuur me een link
          </AuthSubmit>
        </AuthFields>

        {error && <AuthError>{error}</AuthError>}

        <AuthSwitch question="Weet je het weer?">
          <Link to="/beheer/aanmelden" className={authLinkClass}>
            Aanmelden
          </Link>
        </AuthSwitch>
      </form>
    </AuthShell>
  )
}
